import { clsx } from "clsx";
import { Check, Copy, FileUp, Pencil, UploadCloud } from "lucide-react";
import { AnimatePresence, m } from "motion/react";
import {
  useEffect,
  useRef,
  useState,
  type DragEvent,
  type KeyboardEvent,
  type ReactNode,
} from "react";

import styles from "./Editing.module.css";
import { Spinner } from "./Spinner";

// InlineEdit -------------------------------------------------------------------------------

/**
 * Click-to-edit text, like editing a text box on a canvas: Enter saves, Escape cancels,
 * leaving the field saves. Saving is optimistic; on failure the old value comes back.
 */
export function InlineEdit({
  value,
  label,
  onSave,
  placeholder,
  maxLength,
  variant = "text",
  allowEmpty = false,
}: {
  value: string;
  label: string;
  onSave: (next: string) => Promise<unknown>;
  placeholder?: string;
  maxLength?: number;
  variant?: "title" | "text";
  allowEmpty?: boolean;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  const [optimistic, setOptimistic] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const input = useRef<HTMLInputElement>(null);

  // Show the saved text right away; drop the optimistic copy once the server value matches.
  if (optimistic !== null && optimistic === value) setOptimistic(null);
  const shown = optimistic ?? value;

  useEffect(() => {
    if (editing) input.current?.select();
  }, [editing]);

  const commit = async () => {
    const next = draft.trim();
    setEditing(false);
    if (next === shown || (!next && !allowEmpty)) {
      setDraft(shown);
      return;
    }
    setOptimistic(next);
    setSaving(true);
    setError(null);
    try {
      await onSave(next);
      // If the server stores a normalised value, stop showing ours after a moment.
      window.setTimeout(() => setOptimistic((current) => (current === next ? null : current)), 4000);
    } catch (caught) {
      setOptimistic(null);
      setDraft(value);
      setError(caught instanceof Error ? caught.message : "Could not save.");
    } finally {
      setSaving(false);
    }
  };

  const onKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter") {
      event.preventDefault();
      void commit();
    } else if (event.key === "Escape") {
      setDraft(shown);
      setEditing(false);
    }
  };

  return (
    <div className={clsx(styles.inline, styles[variant])}>
      {editing ? (
        <input
          ref={input}
          aria-label={label}
          className={styles.inlineInput}
          value={draft}
          maxLength={maxLength}
          placeholder={placeholder}
          onChange={(event) => setDraft(event.target.value.replace(/[\r\n]/g, ""))}
          onBlur={() => void commit()}
          onKeyDown={onKeyDown}
        />
      ) : (
        <button
          type="button"
          className={styles.inlineButton}
          onClick={() => {
            setDraft(shown);
            setEditing(true);
          }}
        >
          {/* The visible text stays part of the name (WCAG 2.5.3); the action is added before it. */}
          <span className="visually-hidden">{`Edit ${label}:`}</span>{" "}
          <span className={clsx(styles.inlineText, !shown && styles.placeholder)}>
            {shown || placeholder}
          </span>
          <span className={styles.inlineIcon} aria-hidden="true">
            {saving ? <Spinner size={14} /> : <Pencil size={14} />}
          </span>
        </button>
      )}
      {editing && maxLength ? (
        <span className={styles.inlineCounter} aria-hidden="true">
          {draft.length}/{maxLength}
        </span>
      ) : null}
      {error ? (
        <p role="alert" className={styles.inlineError}>
          {error}
        </p>
      ) : null}
    </div>
  );
}

// CopyChip ---------------------------------------------------------------------------------

export function CopyChip({ value, label }: { value: string; label?: string }) {
  const [copied, setCopied] = useState(false);
  useEffect(() => {
    if (!copied) return;
    const timer = window.setTimeout(() => setCopied(false), 1400);
    return () => window.clearTimeout(timer);
  }, [copied]);
  return (
    <button
      type="button"
      className={styles.copy}
      onClick={() => {
        void navigator.clipboard?.writeText(value).then(() => setCopied(true));
      }}
      title={value}
    >
      {/* Name = action + the visible value, so the visible text is part of it (WCAG 2.5.3). */}
      <span className="visually-hidden">{`Copy ${label ?? "value"}:`}</span>{" "}
      <span className={styles.copyText}>{value}</span>
      <span className={styles.copyIcon} aria-hidden="true">
        <AnimatePresence mode="popLayout" initial={false}>
          <m.span
            key={copied ? "done" : "copy"}
            initial={{ scale: 0.4, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.4, opacity: 0 }}
          >
            {copied ? <Check size={13} /> : <Copy size={13} />}
          </m.span>
        </AnimatePresence>
      </span>
      <span className="visually-hidden" aria-live="polite">
        {copied ? "Copied" : ""}
      </span>
    </button>
  );
}

// DropZone ---------------------------------------------------------------------------------

/**
 * Drag files in, or use the button (keyboard and touch). The border animates while files are
 * over it. `compact` fits in toolbars and cards.
 */
export function DropZone({
  onFiles,
  accept,
  title = "Drop documents here",
  description,
  disabled,
  compact,
  children,
}: {
  onFiles: (files: File[]) => void;
  accept: string;
  title?: string;
  description?: ReactNode;
  disabled?: boolean;
  compact?: boolean;
  children?: ReactNode;
}) {
  const [over, setOver] = useState(false);
  const picker = useRef<HTMLInputElement>(null);
  const depth = useRef(0);

  const handle = (event: DragEvent<HTMLDivElement>, kind: "enter" | "leave" | "over" | "drop") => {
    if (disabled || !event.dataTransfer.types.includes("Files")) return;
    event.preventDefault();
    if (kind === "enter") depth.current += 1;
    if (kind === "leave") depth.current -= 1;
    if (kind === "drop") {
      depth.current = 0;
      const files = Array.from(event.dataTransfer.files);
      if (files.length) onFiles(files);
    }
    setOver(depth.current > 0 && kind !== "drop");
  };

  return (
    <div
      className={clsx(styles.drop, compact && styles.compact, over && styles.over)}
      data-disabled={disabled || undefined}
      onDragEnter={(e) => handle(e, "enter")}
      onDragLeave={(e) => handle(e, "leave")}
      onDragOver={(e) => handle(e, "over")}
      onDrop={(e) => handle(e, "drop")}
    >
      <span className={styles.dropBorder} aria-hidden="true" />
      <m.span
        className={styles.dropIcon}
        aria-hidden="true"
        animate={over ? { y: -6, scale: 1.08 } : { y: 0, scale: 1 }}
        transition={{ type: "spring", stiffness: 300, damping: 18 }}
      >
        {over ? <FileUp size={compact ? 20 : 28} /> : <UploadCloud size={compact ? 20 : 28} />}
      </m.span>
      <div className={styles.dropCopy}>
        <p className={styles.dropTitle}>{over ? "Release to upload" : title}</p>
        {description ? <div className={styles.dropText}>{description}</div> : null}
      </div>
      <button
        type="button"
        className={styles.dropButton}
        disabled={disabled}
        onClick={() => picker.current?.click()}
      >
        Browse files
      </button>
      <input
        ref={picker}
        type="file"
        multiple
        accept={accept}
        hidden
        onChange={(event) => {
          const files = Array.from(event.target.files ?? []);
          if (files.length) onFiles(files);
          event.target.value = "";
        }}
      />
      {children}
    </div>
  );
}

// EmptyState -------------------------------------------------------------------------------

export function EmptyState({
  icon,
  title,
  children,
  action,
}: {
  icon: ReactNode;
  title: ReactNode;
  children?: ReactNode;
  action?: ReactNode;
}) {
  return (
    <div className={styles.empty}>
      <m.div
        className={styles.emptyIcon}
        aria-hidden="true"
        animate={{ y: [0, -6, 0] }}
        transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
      >
        {icon}
      </m.div>
      <h3 className={styles.emptyTitle}>{title}</h3>
      {children ? <div className={styles.emptyText}>{children}</div> : null}
      {action}
    </div>
  );
}
